import { TIME_PARSE_FORMAT, TIME_PERIOD, TIME_PERIOD_FORMAT } from '@/roadmaps/constants/roadmaps.constants';
import each from 'lodash/each';
import filter from 'lodash/filter';
import moment from 'moment';

export function formatDateAndTime (date) {
    return moment(date).format('MMM D, YYYY h:mm:ss A z');
}

export function formatDateOnly (date) {
    return moment(date).format('MMM D, YYYY');
}

export function todaysDate () {
    return moment()
        .startOf('day')
        .format(TIME_PARSE_FORMAT);
}

export function calculateRoadmapTimeData ({ items, quarterOneStartingMonth }) {
    const { roadmapStartDate, roadmapEndDate, roadmapDurationInDays } = calculateRoadmapStartEndDuration({ items });
    const timePeriodLabels = calculateTimePeriodLabels(roadmapStartDate, roadmapEndDate, quarterOneStartingMonth);

    return {
        roadmapStartDate,
        roadmapEndDate,
        roadmapDurationInDays,
        timePeriodLabels,
    };
}

function calculateRoadmapStartEndDuration ({ items }) {
    const { firstItemStartDate, lastItemEndDate } = calculateFirstItemStartLastItemEnd({ items });

    firstItemStartDate.startOf(TIME_PERIOD.QUARTER).subtract(1, 'years');
    lastItemEndDate.endOf(TIME_PERIOD.QUARTER).add(3, 'years');

    const roadmapStartDate = firstItemStartDate.format(TIME_PARSE_FORMAT);
    const roadmapEndDate = lastItemEndDate.format(TIME_PARSE_FORMAT);

    return {
        roadmapStartDate,
        roadmapEndDate,
        roadmapDurationInDays: lastItemEndDate.diff(firstItemStartDate, 'days'),
    };
}

function calculateFirstItemStartLastItemEnd ({ items }) {
    const now = moment();
    const startMoments = [now.clone()];
    const endMoments = [now.clone()];

    each(items, (item) => {
        const itemStartMoment = moment(item.startDate, TIME_PARSE_FORMAT);
        const itemEndMoment = moment(item.endDate, TIME_PARSE_FORMAT);

        if (itemStartMoment.isValid() && itemEndMoment.isValid()) {
            startMoments.push(itemStartMoment);
            endMoments.push(itemEndMoment);
        }
    });

    const roadmapStartMoment = moment.min(startMoments);
    const roadmapEndMoment = moment.max(endMoments);

    return {
        firstItemStartDate: roadmapStartMoment,
        lastItemEndDate: roadmapEndMoment,
    };
}

function calculateTimePeriodLabels (roadmapStart, roadmapEnd, quarterOneStartingMonth) {
    const roadmapStartMoment = moment(roadmapStart, TIME_PARSE_FORMAT);
    const roadmapEndMoment = moment(roadmapEnd, TIME_PARSE_FORMAT);
    // quarterOneStartingMonth is 1-indexed and momentJS is 0-indexed, so we need to subtract 1
    // Except in the case where quarterOneStartingMonth is unset i.e. = 0, in this case it should stay 0 i.e. default to January
    const quarterOneStartingMonth0Indexed = quarterOneStartingMonth === 0 ? 0 : quarterOneStartingMonth - 1;
    let periodStartMoment = roadmapStartMoment.clone();
    const timePeriodData = [];
    while (periodStartMoment.isBefore(roadmapEndMoment)) {
        const periodEndMoment = periodStartMoment.clone().endOf(TIME_PERIOD.MONTH);
        const isStartOfAFiscalQuarter = isMomentStartOfAFiscalQuarter(
            periodStartMoment,
            quarterOneStartingMonth0Indexed,
        );

        timePeriodData.push({
            quarterLabel: isStartOfAFiscalQuarter
                ? fiscalQuarterLabel(periodStartMoment, quarterOneStartingMonth0Indexed)
                : '',
            monthLabel: periodStartMoment.format(TIME_PERIOD_FORMAT[TIME_PERIOD.MONTH]),
            yearLabel: periodStartMoment.format(TIME_PERIOD_FORMAT[TIME_PERIOD.YEAR]),
            daysSinceRoadmapStart: periodStartMoment.diff(roadmapStartMoment, 'days'),
            date: periodStartMoment.format(TIME_PARSE_FORMAT),
        });
        periodStartMoment = periodEndMoment.clone().add(1, 'days');
    }

    timePeriodData.push({
        quarterLabel: '',
        monthLabel: '',
        daysSinceRoadmapStart: roadmapEndMoment.diff(roadmapStartMoment, 'days'),
        date: periodStartMoment.format(TIME_PARSE_FORMAT),
    });

    return timePeriodData;
}

function isMomentStartOfAFiscalQuarter (moment, quarterOneStartingMonth) {
    const isStartOfMonth = moment.date() === 1;
    const isMonthStartOfAFiscalQuarter = (moment.month() - quarterOneStartingMonth) % 3 === 0;

    return isStartOfMonth && isMonthStartOfAFiscalQuarter;
}

function fiscalQuarterLabel (periodStartMoment, quarterOneStartingMonth) {
    const shiftedMoment = periodStartMoment.clone().subtract(quarterOneStartingMonth, 'months');

    return shiftedMoment.format(TIME_PERIOD_FORMAT[TIME_PERIOD.QUARTER]);
}

export function calculateDurationInDays (startDate, endDate) {
    const startDateMoment = moment(startDate, TIME_PARSE_FORMAT);
    const endDateMoment = moment(endDate, TIME_PARSE_FORMAT);

    return endDateMoment.diff(startDateMoment, 'days');
}

export function calculateXFromDate ({ date, roadmapStartDate, zoomLevel }) {
    const dateMoment = moment(date, TIME_PARSE_FORMAT);
    const roadmapStartMoment = moment(roadmapStartDate, TIME_PARSE_FORMAT);

    return dateMoment.diff(roadmapStartMoment, 'days') * zoomLevel;
}

export function calculateDateFromX ({ x, roadmapStartDate, zoomLevel }) {
    const roadmapStartMoment = moment(roadmapStartDate, TIME_PARSE_FORMAT);

    const numberOfDays = Math.round(x / zoomLevel);
    const dateMoment = roadmapStartMoment.clone().add(numberOfDays, 'days');

    return dateMoment.format(TIME_PARSE_FORMAT);
}

export function calculateTodayX ({ roadmapStartDate, zoomLevel }) {
    return calculateXFromDate({ date: todaysDate(), roadmapStartDate, zoomLevel });
}

export function shiftDateByDays ({ date, daysToShift }) {
    const dateMoment = moment(date, TIME_PARSE_FORMAT);

    const newDateMoment = dateMoment.clone().add(daysToShift, 'days');

    return newDateMoment.format(TIME_PARSE_FORMAT);
}

export function periodFromDate ({ date, timePeriod, timePeriodLabels, roadmapStartDate, roadmapEndDate }) {
    const timePeriods = filter(
        timePeriodLabels,
        (period) =>
            (timePeriod === TIME_PERIOD.QUARTER && period.quarterLabel !== '') ||
            (timePeriod === TIME_PERIOD.MONTH && period.monthLabel !== ''),
    );

    const dateInDaysFromRoadmapStart = calculateDurationInDays(roadmapStartDate, date);
    for (let index = 0; index < timePeriods.length - 1; index++) {
        if (
            timePeriods[index].daysSinceRoadmapStart <= dateInDaysFromRoadmapStart &&
            dateInDaysFromRoadmapStart < timePeriods[index + 1].daysSinceRoadmapStart
        ) {
            return {
                startDate: timePeriods[index].date,
                endDate: timePeriods[index + 1].date,
            };
        }
    }

    if (dateInDaysFromRoadmapStart > timePeriods.at(-1).daysSinceRoadmapStart) {
        return {
            startDate: timePeriods.at(-1).date,
            endDate: roadmapEndDate,
        };
    }
}

export function clampChildDatesToParent (childStartEndDate, parentStartEndDate) {
    const childDuration = calculateDurationInDays(childStartEndDate.startDate, childStartEndDate.endDate);
    const parentDuration = calculateDurationInDays(parentStartEndDate.startDate, parentStartEndDate.endDate);

    if (childDuration > parentDuration) {
        return {
            childStartDate: parentStartEndDate.startDate,
            childEndDate: parentStartEndDate.endDate,
        };
    }

    let childStartMoment = moment(childStartEndDate.startDate, TIME_PARSE_FORMAT);
    let childEndMoment = moment(childStartEndDate.endDate, TIME_PARSE_FORMAT);

    const parentStartMoment = moment(parentStartEndDate.startDate, TIME_PARSE_FORMAT);
    const parentEndMoment = moment(parentStartEndDate.endDate, TIME_PARSE_FORMAT);

    if (childStartMoment.isBefore(parentStartMoment)) {
        childStartMoment = parentStartMoment.clone();
        childEndMoment = childStartMoment.clone().add(childDuration, 'days');
    }

    if (childEndMoment.isAfter(parentEndMoment)) {
        childEndMoment = parentEndMoment.clone();
        childStartMoment = childEndMoment.clone().subtract(childDuration, 'days');
    }

    return {
        childStartDate: childStartMoment.format(TIME_PARSE_FORMAT),
        childEndDate: childEndMoment.format(TIME_PARSE_FORMAT),
    };
}

export function calculateFirstPeriodScrollAndWidth ({ timePeriod, roadmapStartDate, items, zoomLevel }) {
    const { firstItemStartDate, lastItemEndDate } = calculateFirstItemStartLastItemEnd({ items });

    const roadmapStartDateMoment = moment(roadmapStartDate, TIME_PARSE_FORMAT);
    firstItemStartDate.startOf(timePeriod);
    lastItemEndDate.endOf(timePeriod);

    const firstPeriodScroll = firstItemStartDate.diff(roadmapStartDateMoment, 'days') * zoomLevel;
    const periodWidth = lastItemEndDate.diff(firstItemStartDate, 'days') * zoomLevel;

    return {
        firstPeriodScroll,
        periodWidth,
    };
}
